import moment from 'moment';
import { useContext, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';

import InlineOptions from '../../components/v2/inline-options';
import { MarketChangeContext } from '../../providers/market-change/market-change-provider';
import {
  ChainEndpoint,
  ChainEntry,
  ChainSide,
  MarketChangeChainContext,
} from '../../providers/market-change/market-change-chain-provider';
import { formatTokenPrice } from '../../utils/decimal-map';
import { tokens } from '../../styles/tokens';

// IM-2599 - the Options Chain panel: a per-strike TILE chain. Each strike is
// one row - Calls on the left, Strike in the centre, Puts on the right - and
// each side carries THREE small tiles (T1 / T2 / Change), always shown (no
// click-to-select, no separate detail block - the tiles ARE the chain). The
// tiles reuse the Vol Surface hero's tile chrome (market-change__surface-tile*
// / market-change__chain-tile-iv) wholesale, just at a denser size for the
// 6-tiles-per-row layout (see the --compact modifiers in styles.scss). The
// strike nearest spot is highlighted (tokens.flow.accent), same rule as the
// old table's ATM marker. dOI is out of scope for v1 (IM-2600) - see the
// provider header.
//
// A tile's IV/premia is blank whenever its endpoint failed the provider's
// calibration gate (the expiry was interpolated, not calibrated, at that
// minute) - see market-change-chain-provider's isExpiryCalibrated - or the
// field is simply absent. Blank, never a spurious 0. The Change tile
// additionally needs BOTH endpoints trusted (and both fields finite) before it
// will show a dIV or a premia move - see computeSideDelta/chainDelta below.
// The live 30s T2 repoll (see the provider) updates endpointT2 in place; this
// panel re-renders from context with no extra polling of its own.

const MONO = 'ui-monospace, Menlo, Consolas, monospace';

// Mirrors the Vol Surface Change panel's tile subtitle formatting.
const fmtTimestamp = (ts?: string): string =>
  ts ? `${moment(ts).utc().format('YYYY-MM-DD HH:mm')} UTC` : '';

// Vols display exactly as the surface panel's percentFormat: value*100, 2dp.
const percentFormat = (value: number): string =>
  parseFloat((value * 100).toFixed(2)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
  }) + '%';

// dIV cells: signed vol points, 2dp (e.g. "+1.20" / "-0.80"), matching the
// surface panel's formatDelta exactly (guard -0 so a near-zero delta reads
// "0.00", never "-0.00").
const formatDelta = (value: number): string => {
  const pts = Number((value * 100).toFixed(2));
  const signed = Object.is(pts, -0) ? 0 : pts;
  const sign = signed > 0 ? '+' : '';
  return `${sign}${signed.toFixed(2)}%`;
};

// Signed-direction colour for a formatted dIV string - teal up / orange down,
// the same CSS vars as the surface panel's market-change__ds-up/down classes
// (tokens.charts.turquoise/orange resolve to the identical --color-charts-*
// custom properties as $chart-turquoise/$chart-orange). This chain is a plain
// MUI table, not the ag-grid TableGrid those classes are scoped to, so the
// colour is applied directly via sx/style rather than that class.
const deltaColor = (formatted: string): string | undefined => {
  if (formatted.startsWith('+')) return tokens.charts.turquoise;
  if (formatted.startsWith('-')) return tokens.charts.orange;
  return undefined;
};

// IM-2599 detail block: price in BTC, 4dp with a unit suffix (e.g. "0.0200
// BTC"). Blank (never "0.0000 BTC") when the field is absent/untrusted.
const formatBtc = (value?: number): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(4)} BTC` : '';

// Price in USD, thousands-grouped, no decimals, unit-suffixed to align with the
// BTC line (e.g. "1,655 USD"). Blank when absent/untrusted.
const formatUsd = (value?: number): string =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${Math.round(value).toLocaleString()} USD`
    : '';

// Display-only: blank string -> '-', matching this panel's own tile
// convention below. The underlying data stays '' (blank); this is presentation
// only, never fed back into a gating check.
const dashIfBlank = (value: string): string => value || '-';

// The strike-row key numerically nearest spot, or null when spot is absent/
// non-finite or there are no rows - no fallback guess, so "nearest" is only
// ever claimed when a real spot exists (chain-card's rule: no amber marker
// without a spot). An exact tie keeps the first (lowest) row found scanning
// ascending rows. PURE.
function nearestStrikeKeyToSpot(
  strikeRows: string[],
  spot: number | null | undefined,
): string | null {
  if (typeof spot !== 'number' || !Number.isFinite(spot) || strikeRows.length === 0) return null;
  return strikeRows.reduce((best, key) =>
    Math.abs(Number(key) - spot) < Math.abs(Number(best) - spot) ? key : best,
  );
}

// IM-2599 tiles: price-move formatters mirroring the surface panel's
// formatDelta/formatFutureDelta - signed, round first then guard -0 (so a
// sub-cent/sub-tick move reads unsigned "0.0000 BTC"/"$0", never "-0.0000
// BTC"), magnitude via the same formatters as the snapshot cells above.
const formatBtcDelta = (value?: number): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const rounded = Number(value.toFixed(4));
  const signed = Object.is(rounded, -0) ? 0 : rounded;
  const sign = signed > 0 ? '+' : signed < 0 ? '-' : '';
  return `${sign}${Math.abs(signed).toFixed(4)} BTC`;
};

const formatUsdDelta = (value?: number): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const rounded = Math.round(value);
  const signed = Object.is(rounded, -0) ? 0 : rounded;
  const sign = signed > 0 ? '+' : signed < 0 ? '-' : '';
  return `${sign}${Math.abs(signed).toLocaleString()} USD`;
};

// IM-2624: option (greek) delta for the chain's outer columns - 2dp, shown as-is
// (calls positive, puts negative). Blank when absent/untrusted, never a spurious
// 0 - same blank-not-zero convention as the premia/IV cells.
const formatGreekDelta = (value?: number): string =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '';

// One side's premia for a tile - BTC on top, USD below, matching the chain's
// Mark cell formatting exactly.
export interface ChainTileSide {
  priceBtc: string;
  priceUsd: string;
}

// One side-tile's content: the headline IV (plain percent for T1/T2, signed
// vol points for Change) plus that side's premia.
export interface ChainSideTile extends ChainTileSide {
  iv: string;
}

// One side's three tiles for a strike - T1, T2, Change.
export interface ChainSideTiles {
  t1: ChainSideTile;
  t2: ChainSideTile;
  change: ChainSideTile;
}

export interface ChainTileRow {
  strikeKey: string;
  strikeLabel: string;
  // True for the strike-row key numerically nearest spot - the amber ATM
  // marker, same rule as the old table's nearestStrikeToSpot. False for every
  // row (never a guessed fallback) when spot itself is absent.
  isNearestToSpot: boolean;
  call: ChainSideTiles;
  put: ChainSideTiles;
  // IM-2624: the current (T2 = "now") option delta for the outer delta columns.
  // The tiles already carry how the instruments changed; this frames the chain
  // with each instrument's delta as it stands now. '' when T2 is absent/untrusted.
  callDelta: string;
  putDelta: string;
}

// T2-T1 for one side's priceBtc/priceUsd, each diffed independently - a field
// missing on either side is omitted (blank downstream), never defaulted to a
// partial/zero diff. PURE.
function computeSideDelta(
  s1?: ChainSide,
  s2?: ChainSide,
): { priceBtc?: number; priceUsd?: number } {
  const out: { priceBtc?: number; priceUsd?: number } = {};
  if (
    typeof s1?.priceBtc === 'number' &&
    Number.isFinite(s1.priceBtc) &&
    typeof s2?.priceBtc === 'number' &&
    Number.isFinite(s2.priceBtc)
  ) {
    out.priceBtc = s2.priceBtc - s1.priceBtc;
  }
  if (
    typeof s1?.priceUsd === 'number' &&
    Number.isFinite(s1.priceUsd) &&
    typeof s2?.priceUsd === 'number' &&
    Number.isFinite(s2.priceUsd)
  ) {
    out.priceUsd = s2.priceUsd - s1.priceUsd;
  }
  return out;
}

// The IM-2599 tile chain: one row per strike, each side (call/put) carrying
// its own T1/T2/Change tile trio. T1/T2 read a strike only off an endpoint
// that is BOTH trusted and carries it (same calibration gate as before) -
// absent/untrusted formats to '' (blank), never 0. IV is the SINGLE smile vol
// for the strike (ChainEntry.iv) - read once per endpoint and surfaced
// identically on both the call and put tile for that timestamp (that's
// correct: calls and puts share the smile vol). The Change tile's IV reuses
// the provider's chainDelta (itself null unless both endpoints are trusted -
// see computeChainDelta/chainEndpointsSuspect); its premia change requires the
// SAME both-trusted gate, enforced here via computeSideDelta only ever being
// called when both endpoints are trusted. PURE.
export function buildChainTileRows(
  strikeRows: string[],
  t1: ChainEndpoint | null,
  t2: ChainEndpoint | null,
  chainDelta: Record<string, number> | null,
  spot: number | null | undefined,
  token: string,
): ChainTileRow[] {
  const nearestKey = nearestStrikeKeyToSpot(strikeRows, spot);
  const bothTrusted = Boolean(t1?.trusted && t2?.trusted);

  return strikeRows.map((key) => {
    const e1 = t1?.trusted ? t1.strikes[key] : undefined;
    const e2 = t2?.trusted ? t2.strikes[key] : undefined;
    const dIv = chainDelta?.[key];
    const changeIv = typeof dIv === 'number' && Number.isFinite(dIv) ? formatDelta(dIv) : '';

    const buildSideTiles = (
      pick: (entry?: ChainEntry) => ChainSide | undefined,
    ): ChainSideTiles => {
      const s1 = pick(e1);
      const s2 = pick(e2);
      const sideDelta = bothTrusted ? computeSideDelta(s1, s2) : {};
      return {
        t1: {
          iv: e1 && Number.isFinite(e1.iv) ? percentFormat(e1.iv) : '',
          priceBtc: formatBtc(s1?.priceBtc),
          priceUsd: formatUsd(s1?.priceUsd),
        },
        t2: {
          iv: e2 && Number.isFinite(e2.iv) ? percentFormat(e2.iv) : '',
          priceBtc: formatBtc(s2?.priceBtc),
          priceUsd: formatUsd(s2?.priceUsd),
        },
        change: {
          iv: changeIv,
          priceBtc: formatBtcDelta(sideDelta.priceBtc),
          priceUsd: formatUsdDelta(sideDelta.priceUsd),
        },
      };
    };

    // IM-2624: the outer delta columns show the current (T2) delta only - "the
    // market now". Reads the trusted T2 endpoint (e2 is already gated above);
    // blank when T2 is absent/untrusted.
    const t2Delta = (pick: (entry?: ChainEntry) => ChainSide | undefined): string =>
      formatGreekDelta(pick(e2)?.delta);

    return {
      strikeKey: key,
      strikeLabel: formatTokenPrice(Number(key), token),
      isNearestToSpot: key === nearestKey,
      call: buildSideTiles((entry) => entry?.call),
      put: buildSideTiles((entry) => entry?.put),
      callDelta: t2Delta((entry) => entry?.call),
      putDelta: t2Delta((entry) => entry?.put),
    };
  });
}

// Snapshot tile premia stack - BTC on top, USD muted below - the same shape as
// the ladder's MarkCell, just a plain stack (not a table cell).
const TilePremiaSnapshot = ({ side }: { side: ChainTileSide }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
    <Box component="span" sx={{ fontFamily: MONO, fontSize: 12.5 }}>
      {dashIfBlank(side.priceBtc)}
    </Box>
    <Box component="span" sx={{ color: tokens.text.muted, fontSize: 10, fontFamily: MONO }}>
      {dashIfBlank(side.priceUsd)}
    </Box>
  </Box>
);

// Change-tile premia stack - both BTC and USD carry the signed-direction
// colour (teal/orange, via the same deltaColor used for dIV) rather than the
// snapshot stack's muted USD line, since both figures are themselves signed
// moves here.
const TilePremiaChange = ({ side }: { side: ChainTileSide }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
    <Box
      component="span"
      sx={{ fontFamily: MONO, fontSize: 12.5 }}
      style={{ color: deltaColor(side.priceBtc) }}
    >
      {dashIfBlank(side.priceBtc)}
    </Box>
    <Box
      component="span"
      sx={{ fontFamily: MONO, fontSize: 10 }}
      style={{ color: deltaColor(side.priceUsd) }}
    >
      {dashIfBlank(side.priceUsd)}
    </Box>
  </Box>
);

// One side-tile (T1 / T2 / Change) - reuses the surface hero's tile shell
// (market-change__surface-tile) plus the large-IV line
// (market-change__chain-tile-iv) wholesale, at the compact size needed for six
// tiles per strike row (see the --compact modifiers in styles.scss). No
// title/sub here - the T1/T2/Change label sits once in the panel's head row,
// not repeated per strike.
const ChainSideTileCard = ({ tile, isChange }: { tile: ChainSideTile; isChange?: boolean }) => {
  const Premia = isChange ? TilePremiaChange : TilePremiaSnapshot;
  return (
    <div className="market-change__surface-tile market-change__surface-tile--compact market-change__chain-tile">
      <div className="market-change__chain-tile-premia">
        <Premia side={tile} />
      </div>
      <div
        className="market-change__chain-tile-iv market-change__chain-tile-iv--compact market-change__chain-tile-iv--inline"
        style={{ color: isChange ? deltaColor(tile.iv) : undefined }}
      >
        {dashIfBlank(tile.iv)}
      </div>
    </div>
  );
};

const OptionsChainPanel = () => {
  const { market } = useContext(MarketChangeContext);
  const {
    endpointT1,
    endpointT2,
    chainDelta,
    strikeRows,
    expiries,
    selectedExpiry,
    setSelectedExpiry,
    surfaceType,
    loading,
    error,
  } = useContext(MarketChangeChainContext);

  // Spot for the chain's spot-highlight comes off whichever endpoint has
  // resolved it, preferring T2 (the live/most-recent side) - see
  // market-change-chain-provider's spot field.
  const spot = endpointT2?.spot ?? endpointT1?.spot ?? null;

  const tileRows = useMemo(
    () => buildChainTileRows(strikeRows, endpointT1, endpointT2, chainDelta, spot, market),
    [strikeRows, endpointT1, endpointT2, chainDelta, spot, market],
  );

  const expiryOptions = useMemo(
    () => expiries.map((expiry) => ({ value: expiry, label: expiry })),
    [expiries],
  );

  const hasData = tileRows.length > 0;

  if (surfaceType === 'generated') {
    return (
      <section className="market-change__panel market-change__panel--hero">
        <h3 className="market-change__panel-title">Options Chain</h3>
        <p className="market-change__panel-note">No instrument chain for generated surfaces</p>
      </section>
    );
  }

  return (
    <section className="market-change__panel market-change__panel--hero">
      <div className="market-change__surface-head">
        <h3 className="market-change__panel-title">Options Chain</h3>
        <InlineOptions
          options={expiryOptions}
          selectedOption={selectedExpiry}
          onOptionChange={(value) => setSelectedExpiry(value as string)}
          optionDisabled={loading}
          isCalculateVisibleCount
        />
      </div>

      {hasData && (
        <div className="market-change__chain-meta">
          <span className="market-change__surface-tile-sub">
            T1: {fmtTimestamp(endpointT1?.timestamp)}
          </span>
          <span className="market-change__surface-tile-sub">
            T2: {fmtTimestamp(endpointT2?.timestamp)}
          </span>
        </div>
      )}

      {error && <p className="market-change__panel-error">{error}</p>}

      {loading && !hasData && (
        <div className="market-change__surface-state">
          <CircularProgress size={20} />
          Loading options chain...
        </div>
      )}

      {!loading && !hasData && !error && (
        <div className="market-change__surface-state">No data for this selection</div>
      )}

      {hasData && (
        <div>
          {/* Head row - Calls/Puts group labels, T1/T2/Change sub-labels once
              (not repeated per strike row below). */}
          <div className="market-change__chain-head-row">
            <div className="market-change__chain-head-delta">Delta</div>
            <div className="market-change__chain-head-group">
              <span className="market-change__surface-tile-title">Calls</span>
              <div className="market-change__chain-head-sub">
                <span>T1</span>
                <span>T2</span>
                <span>Change</span>
              </div>
            </div>
            <div className="market-change__chain-head-strike">Strike</div>
            <div className="market-change__chain-head-group market-change__chain-head-group--puts">
              <span className="market-change__surface-tile-title">Puts</span>
              <div className="market-change__chain-head-sub">
                <span>T1</span>
                <span>T2</span>
                <span>Change</span>
              </div>
            </div>
            <div className="market-change__chain-head-delta">Delta</div>
          </div>

          {tileRows.map((row) => (
            <div className="market-change__chain-row" key={row.strikeKey}>
              <div className="market-change__chain-row-delta">{dashIfBlank(row.callDelta)}</div>
              <div className="market-change__chain-row-group">
                <ChainSideTileCard tile={row.call.t1} />
                <ChainSideTileCard tile={row.call.t2} />
                <ChainSideTileCard tile={row.call.change} isChange />
              </div>
              <div
                className="market-change__chain-row-strike"
                // Inline (not sx/className) so the amber ATM marker is
                // assertable, same pattern as the old table's Strike cell.
                style={{
                  color: row.isNearestToSpot ? tokens.flow.accent : tokens.text.secondary,
                }}
              >
                {row.strikeLabel}
              </div>
              <div className="market-change__chain-row-group">
                <ChainSideTileCard tile={row.put.t1} />
                <ChainSideTileCard tile={row.put.t2} />
                <ChainSideTileCard tile={row.put.change} isChange />
              </div>
              <div className="market-change__chain-row-delta">{dashIfBlank(row.putDelta)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default OptionsChainPanel;
